import { ChevronLeft, ChevronRight, Info, ListFilter, Search, TriangleAlert } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"

import { SortHeader, type SortState } from "@/components/rules/sort-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Inactive } from "@/components/ui/inactive"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumeric,
  TableRow,
} from "@/components/ui/table"
import { InfoTip } from "@/components/ui/tooltip"
import {
  listRules,
  setRuleActive,
  type Rule,
  type RuleType,
  type RulesPage,
} from "@/lib/api"
import { EMPTY, formatDate, formatInt, formatRuntime, formatText, formatTime, scoreDirection } from "@/lib/format"
import { cn } from "@/lib/utils"

/** Значение «все» для выпадающих фильтров: пустая строка в Radix Select запрещена. */
const ALL = "__all__"

const PAGE_SIZES = [25, 50, 100] as const

export interface RulesViewProps {
  type: RuleType
  /** Заголовок панели — рядом с ним показываем настоящий total из ручки. */
  title: string
  /** Расширенный набор колонок вкладки Custom rules. */
  extended?: boolean
  /** Группировать строки по категории — как в образце вкладки Default rules. */
  grouped?: boolean
  defaultSort: SortState
  actions?: React.ReactNode
}

/**
 * Таблица правил — общая для вкладок Default и Custom.
 *
 * Данные только настоящие: список приходит из `/api/scoring/rules` (импорт 189 +
 * 458), тумблер пишет в Postgres через PATCH и **перерисовывает строку ответом
 * сервера**, а не тем, что нажали. Разница принципиальная: локальный оптимизм
 * показал бы «включено» и в случае, когда запись не прошла, а перезагрузка
 * страницы вернула бы прежнее состояние — то есть экран врал бы ровно про то,
 * что от него требуется доказать.
 */
export function RulesView({
  type,
  title,
  extended = false,
  grouped = false,
  defaultSort,
  actions,
}: RulesViewProps) {
  const [search, setSearch] = React.useState("")
  const [query, setQuery] = React.useState("")
  const [category, setCategory] = React.useState<string>(ALL)
  const [action, setAction] = React.useState<string>(ALL)
  const [sortState, setSortState] = React.useState<SortState>(defaultSort)
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState<number>(100)
  const [reload, setReload] = React.useState(0)

  const [data, setData] = React.useState<RulesPage | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [selected, setSelected] = React.useState<ReadonlySet<number>>(new Set())
  const [pending, setPending] = React.useState<ReadonlySet<number>>(new Set())
  const [toggleError, setToggleError] = React.useState<string | null>(null)

  // Поиск с задержкой: иначе на каждую букву уходит запрос, а 458 строк
  // возвращаются достаточно быстро, чтобы этот поток был заметен серверу.
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    listRules(
      {
        type,
        q: query || undefined,
        category: category === ALL ? undefined : category,
        action: action === ALL ? undefined : action,
        sort: sortState.sort,
        order: sortState.order,
        page,
        page_size: pageSize,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result)
        setError(null)
        setSelected(new Set())
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [type, query, category, action, sortState, page, pageSize, reload])

  const items = data?.items ?? []

  // Варианты фильтров берём из настоящих значений страницы. Своего списка
  // категорий не заводим: в выгрузке SEON колонок `category` и `perf check`
  // нет (`reference/seon_*_rules.csv`), поля в базе пустые, и придумать им
  // значения значило бы показать аналитику несуществующую разметку правил.
  const categories = React.useMemo(
    () => [...new Set(items.map((rule) => rule.category).filter((value): value is string => Boolean(value)))].sort(),
    [items],
  )
  const actionTypes = React.useMemo(
    () => [...new Set(items.map((rule) => rule.action).filter((value): value is string => Boolean(value)))].sort(),
    [items],
  )

  const missingCategories = !loading && items.length > 0 && categories.length === 0

  async function toggle(rule: Rule, next: boolean) {
    setPending((current) => new Set(current).add(rule.id))
    setToggleError(null)
    try {
      const updated = await setRuleActive(rule.id, next)
      setData((current) =>
        current
          ? {
              ...current,
              items: current.items.map((row) => (row.id === updated.id ? updated : row)),
            }
          : current,
      )
    } catch (cause) {
      // Строка остаётся в прежнем состоянии — том, которое сейчас в базе.
      setToggleError(
        `${rule.seon_id}: ${cause instanceof Error ? cause.message : "update failed"}`,
      )
    } finally {
      setPending((current) => {
        const next = new Set(current)
        next.delete(rule.id)
        return next
      })
    }
  }

  function toggleSelection(id: number) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = items.length > 0 && items.every((rule) => selected.has(rule.id))
  const someSelected = items.some((rule) => selected.has(rule.id))

  const total = data?.total ?? 0
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const pages = data?.pages ?? 1
  const columnCount = extended ? 10 : 5

  return (
    <div className="p-5">
      <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="flex items-baseline gap-2">
            <h2 className="card-title">{title}</h2>
            <span className="text-xs text-fg-muted">
              {loading && !data ? EMPTY : formatInt(total)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </header>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle"
              strokeWidth={2}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              aria-label="Search rules by name or ID"
              className="w-56 pl-7"
            />
          </div>

          <FilterSelect
            label="Category"
            value={category}
            options={categories}
            onChange={(value) => {
              setCategory(value)
              setPage(1)
            }}
            emptyReason="The SEON export has no category column, so there is nothing to filter by yet."
          />

          <FilterSelect
            label="Action"
            value={action}
            options={actionTypes}
            onChange={(value) => {
              setAction(value)
              setPage(1)
            }}
            emptyReason="No action types in the current result set."
          />

          <Inactive reason="Extra filters are not part of this console." size="md">
            <ListFilter className="h-3.5 w-3.5" strokeWidth={2} />
            Add Filter
          </Inactive>

          {someSelected && (
            <span className="ml-auto text-xs text-fg-muted">
              {formatInt(selected.size)} selected
            </span>
          )}
        </div>

        {missingCategories && (
          <p className="flex items-start gap-2 border-b border-line bg-surface-sunken px-4 py-2 text-xs leading-relaxed text-fg-muted">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span>
              The SEON rule export carries no <span className="font-mono">category</span> or{" "}
              <span className="font-mono">perf check</span> values — those cells stay empty until a
              future export includes them. Nothing is failing to load.
            </span>
          </p>
        )}

        {toggleError && (
          <p className="flex items-center gap-2 border-b border-line bg-decline/5 px-4 py-2 text-xs text-decline">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            Could not save the switch — {toggleError}
          </p>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-9">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(value) =>
                      setSelected(value === true ? new Set(items.map((rule) => rule.id)) : new Set())
                    }
                    aria-label="Select all rules on this page"
                  />
                </TableHead>
                <SortHeader column="active" state={sortState} onSort={setSortState} className="w-20">
                  On/Off
                </SortHeader>
                <SortHeader column="seon_id" state={sortState} onSort={setSortState} className="w-28">
                  Id
                </SortHeader>
                <SortHeader column="name" state={sortState} onSort={setSortState}>
                  Name
                </SortHeader>
                {/* ACTION показывает `actionDetails`; сортировать по нему ручка
                    не умеет (в белом списке только `action`, а он у всех 647
                    правил один — `SCORE`), поэтому колонка без стрелок. */}
                <TableHead align="right" className="w-24">
                  Action
                </TableHead>
                {extended && (
                  <>
                    <SortHeader column="category" state={sortState} onSort={setSortState} className="w-44">
                      Category
                    </SortHeader>
                    <SortHeader column="created" state={sortState} onSort={setSortState} className="w-28">
                      Created
                    </SortHeader>
                    <SortHeader column="last_updated" state={sortState} onSort={setSortState} className="w-28">
                      Last updated
                    </SortHeader>
                    <SortHeader column="p95_runtime" state={sortState} onSort={setSortState} className="w-32">
                      <span className="inline-flex items-center gap-1">
                        P95 runtime
                        <InfoTip label="95th percentile of the rule evaluation time, as reported in the SEON export.">
                          <Info className="h-3 w-3 text-fg-subtle" strokeWidth={2.25} />
                        </InfoTip>
                      </span>
                    </SortHeader>
                    <TableHead className="w-28">Perf check</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading && items.length === 0 && <LoadingRows columns={columnCount} />}

              {!loading && error && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columnCount} className="px-4 py-10 text-center">
                    <p className="text-sm font-medium text-decline">{error}</p>
                    <p className="mt-1 text-xs text-fg-muted">
                      The Admin API did not answer. The table stays empty on purpose — it is not
                      showing stale numbers.
                    </p>
                    <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {!loading && !error && items.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columnCount} className="px-4 py-10 text-center">
                    <p className="text-sm text-fg">
                      {query || category !== ALL || action !== ALL
                        ? "No rules match these filters."
                        : "No rules imported yet."}
                    </p>
                    {!query && category === ALL && action === ALL && (
                      <p className="mt-1 text-xs text-fg-muted">
                        Run the import first: <span className="font-mono">POST /api/scoring/import</span>
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {groupRows(items, grouped).map((entry) =>
                entry.kind === "group" ? (
                  <TableRow key={`group-${entry.category}`} className="hover:bg-transparent">
                    <TableCell
                      colSpan={columnCount}
                      className="bg-surface-sunken px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.04em] text-fg"
                    >
                      {entry.category}
                    </TableCell>
                  </TableRow>
                ) : (
                  <RuleRow
                    key={entry.rule.id}
                    rule={entry.rule}
                    extended={extended}
                    selected={selected.has(entry.rule.id)}
                    onSelect={() => toggleSelection(entry.rule.id)}
                    busy={pending.has(entry.rule.id)}
                    onToggle={(next) => void toggle(entry.rule, next)}
                  />
                ),
              )}
            </TableBody>
          </Table>
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-fg-muted">
              {formatInt(from)} - {formatInt(to)} of {formatInt(total)}
            </span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPage(1)
              }}
            >
              <SelectTrigger className="h-7 w-[72px]" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
              Prev
            </Button>
            <Button
              size="sm"
              disabled={page >= pages || loading}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Button>
          </div>
        </footer>
      </section>
    </div>
  )
}

// ── строки ───────────────────────────────────────────────────────────────────

type RowEntry =
  | { kind: "group"; category: string }
  | { kind: "rule"; rule: Rule }

/**
 * Разбивка на группы по категории — как заголовки `Device Rules` в образце.
 *
 * Если у правил категории нет (сейчас так и есть: в выгрузке нет такой колонки),
 * группировка не включается вовсе. Один заголовок «Uncategorized» над 189
 * строками не несёт ничего, кроме лишней строки.
 */
function groupRows(items: Rule[], grouped: boolean): RowEntry[] {
  if (!grouped) return items.map((rule) => ({ kind: "rule", rule }))

  const entries: RowEntry[] = []
  let current: string | null = null
  for (const rule of items) {
    if (rule.category && rule.category !== current) {
      current = rule.category
      entries.push({ kind: "group", category: rule.category })
    }
    entries.push({ kind: "rule", rule })
  }
  return entries
}

function RuleRow({
  rule,
  extended,
  selected,
  onSelect,
  busy,
  onToggle,
}: {
  rule: Rule
  extended: boolean
  selected: boolean
  onSelect: () => void
  busy: boolean
  onToggle: (next: boolean) => void
}) {
  const direction = scoreDirection(rule.action_details)

  return (
    <TableRow className={cn(selected && "bg-accent-soft/50")}>
      <TableCell className="w-9">
        <Checkbox
          checked={selected}
          onCheckedChange={onSelect}
          aria-label={`Select rule ${rule.seon_id}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={rule.active}
          disabled={busy}
          onCheckedChange={onToggle}
          aria-label={`Rule ${rule.seon_id} enabled`}
        />
      </TableCell>
      <TableCell className="text-fg-muted">{rule.seon_id}</TableCell>
      <TableCell className="text-fg">
        {/* Название ведёт в карточку — только чтение: условие правила и,
            если условия нет, причина словами движка. */}
        <Link
          to={`/scoring/rules/${rule.id}`}
          className="transition-colors duration-150 ease-snap hover:text-accent hover:underline"
        >
          {formatText(rule.name)}
        </Link>
      </TableCell>
      <TableNumeric
        className={cn(
          "font-medium",
          direction === "decrease" && "text-approve",
          direction === "increase" && "text-decline",
          direction === "neutral" && "text-fg-muted",
        )}
      >
        {formatText(rule.action_details)}
      </TableNumeric>
      {extended && (
        <>
          <TableCell className="text-fg-muted">{formatText(rule.category)}</TableCell>
          <TableCell className="whitespace-nowrap text-xs">
            <span className="block text-fg">{formatDate(rule.created)}</span>
            <span className="block text-fg-subtle">{formatTime(rule.created)}</span>
          </TableCell>
          <TableCell className="whitespace-nowrap text-xs">
            <span className="block text-fg">{formatDate(rule.last_updated)}</span>
            <span className="block text-fg-subtle">{formatTime(rule.last_updated)}</span>
          </TableCell>
          <TableCell className="text-fg-muted">{formatRuntime(rule.p95_runtime)}</TableCell>
          <TableCell>
            {rule.perf_check ? (
              <Badge tone={rule.perf_check.toUpperCase() === "PASSED" ? "approve" : "review"}>
                {rule.perf_check}
              </Badge>
            ) : (
              <span className="text-fg-subtle">{EMPTY}</span>
            )}
          </TableCell>
        </>
      )}
    </TableRow>
  )
}

function LoadingRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 8 }, (_, row) => (
        <TableRow key={row} className="hover:bg-transparent">
          {Array.from({ length: columns }, (_, column) => (
            <TableCell key={column}>
              <Skeleton className="h-3 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

/**
 * Выпадающий фильтр. Когда значений нет, вместо пустого списка показываем
 * выключенный элемент с объяснением причины — пустой раскрывающийся список
 * читается как сломанный запрос.
 */
function FilterSelect({
  label,
  value,
  options,
  onChange,
  emptyReason,
}: {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  emptyReason: string
}) {
  if (options.length === 0) {
    return (
      <Inactive reason={emptyReason} size="md">
        {label}
      </Inactive>
    )
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="min-w-[128px]" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: all</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
